import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import {
  Dropdown,
  Menu,
  Image,
  Button,
  withApplicationTheme,
  ProductLine,
} from '@adc/ui-core'
import classNames from 'classnames/bind'
import { withApplicationContext } from '../auctionApplication/context'
import auctioncomIconLogo from '../../img/auction-logo.svg'
import smartsaleIconLogo from '../../img/smartsale-logo.svg'
import auctioncomLogo from '../../img/logo-light.svg'
import smartsaleLogo from '../../img/smartsale-powered-logo.svg'
import Search from '../search'
import NativeAppBanner from '../nativeAppBanner'
import BellNotificationsDrawer, {
  BellNotificationPropType,
} from '../bellNotificationsDrawer'
import { LOGOUT_URL, onLogoutHandler } from '../../util/logout'
import MenuItem from './menuItem'
import MobileHeader from './mobileHeader'
import {
  generalRoutes,
  aboutUs,
  getSellLinkData,
  getSmartSaleBuyLinkData,
} from './constants/routes'
import LogoHeader from './logoHeader'
import Nudge from './nudge'
import {
  getNewBuyDropdown,
  loggedInDropdown,
  smartSaleLoggedInDropdown,
  learnDropdown,
} from './constants/menus'
import cls from './styles.scss'

const cx = classNames.bind(cls)

const menuPositionRight = { position: 'right' }

const HEADER_BUY_ELMID = 'h_buy_menu'
const HEADER_LEARN_ELMID = 'h_learn_menu'
const HEADER_USER_ELMID = 'h_user_menu'
const BELL_NOTIFICATIONS_ELMID = 'bell_notifications'
const TRANSITION_SCROLL_OFFSET = 90 // roughly, height of desktop header
const MAINTENANCE_BANNER_DISABLED_STRING = 'false'

const LogoutItem = (
  <MenuItem
    {...generalRoutes.logOut}
    onClick={onLogoutHandler}
    className={cx(generalRoutes.logOut.className)}
  />
)

const NotificationBadge = ({ bellNotificationsCount, className, isInMenu }) =>
  bellNotificationsCount !== '' && (
    <span
      className={cx('bell-notification', className)}
      data-elm-id={
        isInMenu ? 'notification_count_menu_item' : 'notification_count'
      }
    >
      {bellNotificationsCount}
    </span>
  )

NotificationBadge.propTypes = {
  bellNotificationsCount: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  className: PropTypes.string,
  isInMenu: PropTypes.bool,
}

const formatNotificationsCount = (bellNotifications, unreadCount) => {
  let bellNotificationsCount = unreadCount ?? bellNotifications.length

  if (bellNotificationsCount >= 20) {
    bellNotificationsCount = '20+'
  } else if (bellNotificationsCount === 0) {
    bellNotificationsCount = ''
  }
  return bellNotificationsCount
}

// Gets the position of the element in reference to the DOM
const getTopPosition = elem =>
  elem.getBoundingClientRect().top + window.pageYOffset

class Header extends PureComponent {
  constructor(props) {
    super(props)
    this.buyHeaderRef = React.createRef()
    this.helpHeaderRef = React.createRef()
    this.moreHeaderRef = React.createRef()
    this.userHeaderRef = React.createRef()
    this.learnHeaderRef = React.createRef()
    this.dropDownRefs = [
      this.buyHeaderRef,
      this.helpHeaderRef,
      this.moreHeaderRef,
      this.userHeaderRef,
      this.learnHeaderRef,
    ]
  }

  static propTypes = {
    customLogo: PropTypes.string,
    featureFlags: PropTypes.object,
    isSticky: PropTypes.bool,
    onLoginClick: PropTypes.func,
    onSignupClick: PropTypes.func,
    onChatClick: PropTypes.func,
    showLiveChat: PropTypes.bool,
    user: PropTypes.object,
    showSearch: PropTypes.bool,
    showLinks: PropTypes.bool,
    bellNotifications: PropTypes.arrayOf(BellNotificationPropType),
    onClickBellNotifications: PropTypes.func,
    onCloseBellNotifications: PropTypes.func,
    bellNotificationsOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onSearchSubmit: PropTypes.func,
    hidePreferencesTooltip: PropTypes.bool,
    showPurchaseProfileTooltip: PropTypes.bool,
    onDrawerFetchMore: PropTypes.func,
    unreadCount: PropTypes.number,
    transitionElmId: PropTypes.string,
    filters: PropTypes.object,
    theme: PropTypes.object,
    dropdownStyle: PropTypes.oneOf(['primary-button', 'default-button']),
    className: PropTypes.string,
    forceSmartSaleLogo: PropTypes.bool,
    style: PropTypes.object,
    applicationContext: PropTypes.object,
    embeddedContext: PropTypes.string,
  }

  static defaultProps = {
    featureFlags: {},
    isSticky: true,
    onChatClick: _.noop,
    onLoginClick: _.noop,
    onSignupClick: _.noop,
    showLiveChat: false,
    user: { logged_in: false },
    showSearch: true,
    showLinks: true,
    onClose: _.noop,
    hidePreferencesTooltip: false,
    showPurchaseProfileTooltip: false,
    bellNotifications: [],
    onClickBellNotifications: _.noop,
    onCloseBellNotifications: _.noop,
    bellNotificationsOpen: false,
    transitionElmId: '',
    theme: {},
    forceSmartSaleLogo: false,
    dropdownStyle: undefined,
    style: {},
    embeddedContext: '',
  }

  state = {
    notifications: [],
    bellNotificationsOpen: false,
    showSearchOverride: null,
    isClient: false,
  }

  onCloseBellNotifications = () => {
    this.setState({
      bellNotificationsOpen: false,
    })
  }

  onClickBellNotifications = () => {
    this.setState({
      bellNotificationsOpen: true,
    })
  }

  renderAuthActions = () => {
    const {
      user,
      featureFlags,
      hidePreferencesTooltip,
      showPurchaseProfileTooltip,
      onClickBellNotifications,
      bellNotifications,
      unreadCount,
      dropdownStyle: dropdownStylePassedIn,
      showLinks,
      theme,
    } = this.props

    const dropdownStyle =
      dropdownStylePassedIn || theme.productLine === ProductLine.SmartSale
        ? 'default-button'
        : 'primary-button'

    // Is this the price we pay for seamless toolkit updates?
    const addOffers = featureFlags['fe.resi.direct_offers']
    const bellNotificationsCount = formatNotificationsCount(
      bellNotifications,
      unreadCount
    )

    const notificationsElement = {
      className: 'notifications',
      elmId: 'h_not',
      title: (
        <div>
          <NotificationBadge
            bellNotificationsCount={bellNotificationsCount}
            isInMenu
          />{' '}
          Notifications
        </div>
      ),
      onClick: onClickBellNotifications,
    }

    if (this.props.user.logged_in) {
      const loggedInDropdownOptions =
        theme.productLine === ProductLine.SmartSale
          ? smartSaleLoggedInDropdown()
          : [notificationsElement, ...loggedInDropdown(addOffers, featureFlags)]
      const ProfileDropdown = (
        <HeaderDropdown
          buttonStyle={dropdownStyle}
          onDropDownClick={this.onDropDownClick}
          dropdownList={loggedInDropdownOptions}
          text={
            <span className={cx('header-truncate')}>
              {theme.productLine !== ProductLine.SmartSale && (
                <NotificationBadge
                  bellNotificationsCount={bellNotificationsCount}
                  className={cx('top-left')}
                />
              )}{' '}
              Welcome, {user.first_name}
            </span>
          }
          elmId={HEADER_USER_ELMID}
          dropDownRef={this.userHeaderRef}
          direction='left'
        >
          {LogoutItem}
        </HeaderDropdown>
      )
      return (
        <Nudge
          user={user}
          elmId='header_nudge'
          showPreferencesTooltip={!hidePreferencesTooltip}
          showPurchaseProfileTooltip={showPurchaseProfileTooltip}
        >
          {ProfileDropdown}
        </Nudge>
      )
    } else {
      return (
        <Menu.Menu {...(!showLinks ? menuPositionRight : {})}>
          <MenuItem
            {...generalRoutes.signUp}
            onClick={this.props.onSignupClick}
          />
          <MenuItem {...generalRoutes.logIn} onClick={this.props.onLoginClick}>
            <Button role='secondary'>Log In</Button>
          </MenuItem>
        </Menu.Menu>
      )
    }
  }

  // react-semantic has an issue with dropdowns in the menu
  // multiple synthetic events are fired that toggle the active state of a dropdown in a menu
  // while a fix is still worked on we need to manually reset the state
  // hack fix shamelessy inspired from here detailing the defects.
  // https://github.com/Semantic-Org/Semantic-UI-React/issues/2046
  onDropDownClick = elmId => {
    const refsToClose = _.filter(this.dropDownRefs, dropDownRef => {
      return (
        dropDownRef.current &&
        dropDownRef.current.props['data-elm-id'] !== elmId
      )
    })
    _.each(refsToClose, dropDownRef => {
      dropDownRef.current && dropDownRef.current.close()
    })
  }

  /**
   * Sets showSearchOverride based on whether user has scrolled past
   * the indicated transition element.
   */
  scrollEvent = () => {
    const delta =
      getTopPosition(this.state.transitionElem) -
      window.pageYOffset -
      TRANSITION_SCROLL_OFFSET

    if (delta < 0) {
      if (!this.state.showSearchOverride) {
        this.setState({ showSearchOverride: true })
      }
    } else {
      if (this.state.showSearchOverride) {
        this.setState({ showSearchOverride: false })
      }
    }
  }

  /**
   * Adds an event listener relative to elem indicated with transitionElmId
   */
  addScrollListener() {
    const { transitionElmId } = this.props
    if (transitionElmId) {
      const transitionElem = document.querySelector(
        `[data-elm-id="${transitionElmId}"]`
      )
      if (transitionElem) {
        this.setState({ transitionElem })
        window.addEventListener('scroll', this.scrollEvent)
      }
    }
  }

  componentDidUpdate(prevProps) {
    const { showSearch } = this.props

    if (prevProps.showSearch !== showSearch) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        showSearchOverride: showSearch,
      })

      // only need listener when initially not showing search (like Home page)
      if (!showSearch) {
        this.addScrollListener()
      } else {
        window.removeEventListener('scroll', this.scrollEvent)
      }
    }
  }

  componentDidMount() {
    this.setState({
      showSearchOverride: this.props.showSearch,
      isClient: true,
    })

    if (!this.props.showSearch) this.addScrollListener()
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillUnmount() {
    window.removeEventListener('scroll', this.scrollEvent)
  }

  renderAuctionComLinks = () => {
    const { featureFlags, theme } = this.props
    const sellLink = getSellLinkData(theme, featureFlags)
    return (
      <>
        <Menu.Menu {...menuPositionRight}>
          <HeaderDropdown
            onDropDownClick={this.onDropDownClick}
            dropdownList={getNewBuyDropdown(featureFlags)}
            text='Buy'
            direction='left'
            elmId={HEADER_BUY_ELMID}
            dropDownRef={this.buyHeaderRef}
            className='buy-dropdown'
          />
          <MenuItem {...sellLink} className={[sellLink.className]} />
          <HeaderDropdown
            onDropDownClick={this.onDropDownClick}
            dropdownList={learnDropdown}
            text='Learn'
            direction='left'
            elmId={HEADER_LEARN_ELMID}
            dropDownRef={this.learnHeaderRef}
            className='learn-dropdown'
          />
        </Menu.Menu>
        <MenuItem {...aboutUs} className={[aboutUs.className]} />
        {this.renderAuthActions()}
      </>
    )
  }

  renderSmartSaleLinks = () => {
    const { theme, featureFlags } = this.props
    const buyLink = getSmartSaleBuyLinkData()
    const sellLink = getSellLinkData(theme, featureFlags)
    return (
      <>
        <Menu.Menu {...menuPositionRight}>
          <MenuItem {...buyLink} className={[buyLink.className]} />
          <MenuItem {...sellLink} className={[sellLink.className]} />
        </Menu.Menu>
        {this.renderAuthActions()}
      </>
    )
  }

  renderLinks = () => {
    // only render this client-side due to some client-side logic in the links
    if (!this.state.isClient) return null
    return this.props.theme.productLine === ProductLine.SmartSale
      ? this.renderSmartSaleLinks()
      : this.renderAuctionComLinks()
  }

  render() {
    const { theme } = this.props
    const {
      customLogo,
      featureFlags,
      isSticky,
      onSearchSubmit,
      showSearch,
      showLinks,
      user,
      bellNotifications,
      onClickBellNotifications,
      onCloseBellNotifications,
      bellNotificationsOpen,
      onDrawerFetchMore,
      unreadCount,
      filters,
      className,
      forceSmartSaleLogo,
      style,
      embeddedContext: embeddedContextInjected,
      applicationContext,
    } = this.props

    const { embeddedContext: embeddedContextFromAppContext } =
      applicationContext
    const embeddedContext =
      embeddedContextInjected || embeddedContextFromAppContext

    const { showSearchOverride } = this.state
    const showSearchOnDesktop =
      showSearchOverride !== null ? showSearchOverride : showSearch
    const bellNotificationsCount = formatNotificationsCount(
      bellNotifications,
      unreadCount
    )

    let defaultLogoImg
    if (theme.productLine === ProductLine.SmartSale || forceSmartSaleLogo) {
      defaultLogoImg = showSearchOnDesktop ? smartsaleIconLogo : smartsaleLogo
    } else {
      defaultLogoImg = showSearchOnDesktop ? auctioncomIconLogo : auctioncomLogo
    }

    const getDefaultIcon = (iconOnly = false) => (
      <Image
        className={cx('icon', { 'icon-only': iconOnly })}
        src={customLogo || defaultLogoImg}
        data-elm-id='h_logo'
        alt='logo'
      />
    )

    /**
     * In a ideal world, the header would just be told if it has to worry about adjusting for notification, but
     * as we are using this as a part of custom component outside the shell and passed through, we need header
     * to determine itself if it needs to account for hasNotification.
     */
    const bannerMessage = featureFlags['fe.resi.maintenance-banner']
    const hasNotification =
      bannerMessage && bannerMessage !== MAINTENANCE_BANNER_DISABLED_STRING

    return embeddedContext === 'webview' ? null : (
      <header style={style}>
        <div
          data-elm-id='header'
          className={cx(
            'header',
            {
              'sticky-header': isSticky,
              'with-notification': hasNotification,
            },
            className
          )}
        >
          <NativeAppBanner track={_.noop} isSticky={isSticky} />
          <MobileHeader
            featureFlags={featureFlags}
            liveChat={false}
            onLoginClick={this.props.onLoginClick}
            onLogoutClick={onLogoutHandler}
            onSignupClick={this.props.onSignupClick}
            user={user}
            showSearch={showSearch}
            showLinks={showLinks}
            hasNotification={hasNotification}
            onSearchSubmit={onSearchSubmit}
            bellNotifications={bellNotifications}
            onClickBell={onClickBellNotifications}
            bellNotificationsCount={String(bellNotificationsCount)}
            filters={filters}
            forceSmartSaleLogo={forceSmartSaleLogo}
            /**
              Normally, one wouldn't need to pass this through as the context should provide it, however, that doesn't 
              work very well for WebComponents, in which case we can't rely on the context to provide the theme.
            */
            theme={theme}
          />

          <div className={cx('menu')}>
            <Menu className={cx('menu-inner')} borderless size='large' icon>
              <MenuItem url='/'>{getDefaultIcon(showSearchOnDesktop)}</MenuItem>
              {showSearchOnDesktop && (
                <Menu.Item className={cx('search')}>
                  <Search
                    elmId='desktop'
                    filters={filters}
                    onSearchSubmit={onSearchSubmit}
                    user={user}
                    categorizeResults
                  />
                </Menu.Item>
              )}
              {showLinks ? (
                this.renderLinks()
              ) : (
                <Menu.Menu {...menuPositionRight}>
                  {this.renderAuthActions()}
                </Menu.Menu>
              )}
            </Menu>
          </div>
        </div>
        {isSticky && (
          <div
            className={cx('sticky-header-spacer', {
              'with-notification': !!hasNotification,
            })}
          />
        )}
        <BellNotificationsDrawer
          isOpen={bellNotificationsOpen}
          onClose={onCloseBellNotifications}
          notifications={bellNotifications}
          elmId={BELL_NOTIFICATIONS_ELMID}
          onScrollEnd={onDrawerFetchMore}
        />
      </header>
    )
  }
}

export class HeaderDropdown extends PureComponent {
  static propTypes = {
    text: PropTypes.node.isRequired,
    dropdownList: PropTypes.array.isRequired,
    direction: PropTypes.string,
    className: PropTypes.string,
    children: PropTypes.node,
    elmId: PropTypes.string,
    onDropDownClick: PropTypes.func,
    dropDownRef: PropTypes.object,
    buttonStyle: PropTypes.oneOf([
      'primary-button',
      'default-button',
      'menu-item',
    ]),
  }

  onDropDownClick = () => {
    return this.props.onDropDownClick(this.props.elmId)
  }

  render() {
    const {
      dropdownList,
      text,
      direction,
      className,
      elmId,
      dropDownRef,
      children,
      buttonStyle = 'menu-item',
    } = this.props

    return (
      <Dropdown
        onClick={this.onDropDownClick}
        ref={dropDownRef}
        item
        simple
        text={text}
        direction={direction}
        className={cx(className, buttonStyle)}
        data-elm-id={elmId}
        aria-label='dropdown'
      >
        <Dropdown.Menu>
          {dropdownList.map((item, index) => (
            <MenuItem key={index} role='option' {...item} />
          ))}
          {children}
        </Dropdown.Menu>
      </Dropdown>
    )
  }
}

const HeaderWithTheme = withApplicationTheme(withApplicationContext(Header))

HeaderWithTheme.LogoHeader = LogoHeader

export default HeaderWithTheme
export { LOGOUT_URL }
